import { useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import Modal from '../../components/Modal';
import { Banner, PageLoading, Select, TextInput } from '../../components/ui';
import { LeadFieldInput, deskFields, isFilled } from './LeadFields';
import type { Lead, LeadFormApp, LeadStage, LeadStatus } from '../../types';

/**
 * Add a lead from the console — the published lead form, plus a pick of
 * the starting stage and status. A rep usually adds leads on the phone;
 * this is the back-office path.
 */
export default function LeadCreateDialog({
  onClose, onCreated,
}: { onClose: () => void; onCreated: () => void }) {
  const toast = useToast();

  const [form, setForm] = useState<LeadFormApp | null>(null);
  const [stages, setStages] = useState<LeadStage[]>([]);
  const [statuses, setStatuses] = useState<LeadStatus[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [stageId, setStageId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [f, st, ss] = await Promise.all([
          api.get<LeadFormApp>('/leads/form/app'),
          api.get<LeadStage[]>('/leads/stages'),
          api.get<LeadStatus[]>('/leads/statuses'),
        ]);
        if (cancelled) return;
        setForm(f);
        setStages(st);
        setStatuses(ss);
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof ApiClientError ? err.message : 'Could not load the form.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fields = deskFields(form?.fields ?? []);
  const setValue = (key: string, v: unknown) => setValues((prev) => ({ ...prev, [key]: v }));
  const missing = fields.some((f) => f.required && !isFilled(f, values[f.key]));
  const canSubmit = name.trim() !== '' && !missing;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post<Lead>('/leads', {
        name: name.trim(),
        values,
        stageId: stageId || undefined,
        statusId: statusId || undefined,
      });
      toast.success(`${name.trim()} added.`);
      onCreated();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not add that lead.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Add a lead"
      subtitle="The same form your team fills on the app."
      wide
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={busy || !canSubmit || !form}
            onClick={() => void submit()}>
            {busy ? <span className="spinner" /> : null} Add lead
          </button>
        </>
      )}
    >
      {loadError ? <Banner kind="danger">{loadError}</Banner> : null}
      {!form && !loadError ? <PageLoading rows={4} /> : null}

      {form ? (
        <div className="col gap-4">
          <TextInput label="Name *" value={name} placeholder="Company or contact name"
            onChange={(e) => setName(e.target.value)} />

          <div className="row gap-12" style={{ flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 160 }}>
              <Select label="Stage" value={stageId} hint="Defaults to the first pipeline stage."
                onChange={(e) => setStageId(e.target.value)}>
                <option value="">— Default —</option>
                {stages.filter((s) => s.isActive).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </Select>
            </div>
            <div style={{ flex: 1, minWidth: 160 }}>
              <Select label="Status" value={statusId} hint="Defaults to the first open status."
                onChange={(e) => setStatusId(e.target.value)}>
                <option value="">— Default —</option>
                {statuses.filter((s) => s.isActive).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </Select>
            </div>
          </div>

          {fields.map((field) => (
            <LeadFieldInput key={field.key} field={field}
              value={values[field.key]} onChange={(v) => setValue(field.key, v)} />
          ))}
        </div>
      ) : null}
    </Modal>
  );
}
